import { useEffect, useMemo, useRef } from "react";
import { Modal } from "antd";
import { LoadingOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../../../../hooks/useUsers";
import { useWaiterOpenCounts, useWaitersByTurnover } from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  posModalStyles as modalStyles,
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";
import { gridArrowIndex, isArrowKey, scrollMarkedIntoView } from "../listKeyboard";
import { orderNewTableWaiters, type NewTableWaiter } from "./newTableWaiterOrder";

interface NewTableWaiterModalProps {
  open: boolean;
  me: { id: number; name: string };
  /** Маркираната карта — по подразбиране влезлият; при „Назад" от номера —
   * избраният преди това. */
  selectedWaiterId: number;
  /** Докосване на карта — само я маркира. */
  onSelect: (waiter: NewTableWaiter) => void;
  /** „Избери" (Enter) — маркираният, към номера. */
  onConfirm: () => void;
  onClose: () => void;
}

/** Първият модал на „Нова маса" с правото „Откриване и закриване на сметки на
 * други оператори": на кой сервитьор е масата. Докосване на карта или стрелките
 * я маркират, „Избери" (Enter) минава към номера (`NewTableModal`) с маркирания
 * (по подразбиране влезлият), Esc затваря. Без бързи цифри по картите. */
export const NewTableWaiterModal = ({
  open,
  me,
  selectedWaiterId,
  onSelect,
  onConfirm,
  onClose,
}: NewTableWaiterModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: users = [], isLoading, error } = useUsers("ordering", undefined, undefined, {
    enabled: open,
  });
  const { data: openCounts = [] } = useWaiterOpenCounts(open);
  const { data: turnoverOrder = [] } = useWaitersByTurnover(open);
  const waiters = useMemo(
    () =>
      orderNewTableWaiters(
        users,
        me,
        turnoverOrder,
        new Map(openCounts.map((row) => [row.waiterId, row.count])),
      ),
    [users, me, turnoverOrder, openCounts],
  );
  // Картите са по две на ред — на телефон по една (решетката долу).
  const columns = isNarrow ? 1 : 2;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        onConfirm();
        return;
      }
      // Решетката — по общите правила (`listKeyboard.ts`).
      if (isArrowKey(e.key)) {
        e.preventDefault();
        if (waiters.length === 0) return;
        const index = waiters.findIndex((waiter) => waiter.id === selectedWaiterId);
        const next = waiters[gridArrowIndex(e.key, index, waiters.length, columns)];
        if (next.id !== selectedWaiterId) onSelect(next);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onConfirm, onClose, onSelect, waiters, selectedWaiterId, columns]);

  // Маркираната карта — във видимата част на списъка (скролът е скрит).
  useEffect(() => {
    scrollMarkedIntoView(listRef.current, `[data-waiter-id="${selectedWaiterId}"]`);
  }, [selectedWaiterId]);

  const metaOf = (waiter: NewTableWaiter) => {
    const tables =
      waiter.openTables === 0
        ? t("ordering.newTableWaiter.noOpenTables")
        : t("ordering.newTableWaiter.openTables", { count: waiter.openTables });
    return waiter.isMe ? `${t("ordering.newTableWaiter.you")} · ${tables}` : tables;
  };

  // Картите на сервитьорите са по две — клас „Form" (docs: modals.md).
  const width = isMobile ? undefined : isTablet ? 520 : 560;
  const footerHeight = isNarrow ? footerButtonHeight(true) : 56;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      // Esc го поема обработчикът горе (с preventDefault за Esc-навигацията).
      keyboard={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card font-sans text-pos-ink">
        <ModalHeader
          icon={<UserOutlined />}
          title={t("ordering.newTable")}
          subtitle={t("ordering.newTableWaiter.subtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div className={`flex flex-col ${isNarrow ? "gap-3.5 p-4" : "gap-4 px-6 pt-5 pb-6"}`}>
          {isLoading ? (
            <div className="grid min-h-[160px] place-items-center">
              <LoadingOutlined spin className="text-[32px] text-pos-accent" />
            </div>
          ) : (
            <div
              ref={listRef}
              className={`grid max-h-[50vh] gap-2.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                columns === 1 ? "grid-cols-1" : "grid-cols-2"
              }`}
            >
              {waiters.map((waiter) => (
                <WaiterCard
                  key={waiter.id}
                  waiterId={waiter.id}
                  name={waiter.name}
                  meta={metaOf(waiter)}
                  compact={isNarrow}
                  selected={waiter.id === selectedWaiterId}
                  onClick={() => onSelect(waiter)}
                />
              ))}
            </div>
          )}
          {/* Без списъка остава поне „Вие" — масата пак може да се отвори на себе си. */}
          {error && !isLoading && (
            <div role="alert" className="text-[12.5px] font-medium text-pos-danger-ink">
              {String(error)}
            </div>
          )}

          {/* Като в номера на масата — „Назад" тесен (1:3) */}
          <div className="mt-1 grid grid-cols-[1fr_3fr] gap-2.5">
            <GhostButton
              label={t("common.back")}
              onClick={onClose}
              shortcut={isDesktop ? "Esc" : undefined}
              height={footerHeight}
            />
            <PrimaryButton
              label={t("common.select")}
              onClick={onConfirm}
              shortcut={isDesktop ? "⏎" : undefined}
              height={footerHeight}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

function WaiterCard({
  waiterId,
  name,
  meta,
  compact,
  selected,
  onClick,
}: {
  /** За превъртането до маркираната карта (`data-waiter-id`). */
  waiterId: number;
  name: string;
  meta: string;
  /** Телефон — по-малко въздух отгоре и отдолу. */
  compact: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  // Избраният е като избраната маса в списъка (TableCard): цялата карта е с
  // маркиран фон и акцентна рамка. Без hover — иначе картата под мишката
  // изглежда избрана също.
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      aria-pressed={selected}
      data-waiter-id={waiterId}
      className={`flex w-full cursor-pointer flex-col items-start justify-center gap-1 rounded-[14px] border-[1.5px] px-4 text-left transition-colors ${
        compact ? "min-h-[60px] py-2" : "min-h-[78px] py-3"
      } ${selected ? "border-pos-accent bg-pos-marked" : "border-pos-line bg-pos-card"}`}
    >
      <span className="w-full truncate text-[16px] font-bold leading-[1.2] text-pos-ink">
        {name}
      </span>
      <span className="w-full truncate text-[12.5px] font-medium text-pos-soft2">{meta}</span>
    </button>
  );
}
